<template>
  <!--    Contribution Form-->
  <validation-observer
    ref="shareForm"
    v-slot="{invalid}"
  >
    <am-modal
      title="Share Documents"
      :show="shareModalOpened"
      :show-overlay="formLoading"
      size="md"
      @onClose="closeShareModal()"
    >
      <error-display :error="error" />
      <div class="row mb-1">
        <div class="col-12">
          <strong>Files to Share</strong>
        </div>
        <div
          v-for="(file,index) in files"
          :key="index"
          class="col-12"
        >
          <b-badge variant="light-primary">
            <feather-icon icon="CheckIcon" />
            {{ file.name }} - {{ file.type }} ({{ file.size }}MB)
          </b-badge>
        </div>
      </div>
      <am-select
        v-model="shareWith"
        label-name="label"
        value-name="id"
        label="Share With"
        :options="shareOptions"
        :disabled="files.length>1"
      />
      <div v-if="shareWith==='local'">
        <am-select
          v-model="recipientType"
          name="recipientType"
          label="Recipient Type"
          rules="required"
          label-name="label"
          value-name="value"
          :options="[{label:'Members',value:'Members'},{label:'Societies',value:'Organizations'}]"
        />
        <b-form-checkbox
          v-if="recipientType"
          v-model="shareWithAll"
          class="mb-1"
        >
          Share With All {{ recipientType }}
        </b-form-checkbox>
        <am-input
          v-model="shareSubject"
          label="Subject"
          rules="required"
          name="subject"
        />
        <am-rich-text
          v-model="shareMessage"
          label="Message"
          placeholder="Enter some message..."
          name="message"
          rules="required"
          class="mb-1"
        />
        <am-input
          v-if="!shareWithAll"
          v-model="searchTerm"
          name="contributor"
          label="Search Recipients"
          :placeholder="recipientType?(recipientType==='Members'?'Member ID or Name':`Society ID or Name`):null"
          suffix-icon="SearchIcon"
          :readonly="!recipientType"
          @onSuffixIconClick="getRecipientDetails"
          @onEnterPressed="getRecipientDetails"
        />
      </div>
      <div v-if="shareWith==='public' && files.length===1">
        <am-input
          v-model="shareTitle"
          label="Share Title"
        />
        <div
          class="d-flex flex-wrap"
        >
          <ShareNetwork
            v-for="network in networks"
            :key="network.key"
            :network="network.network"
            :url="getPath(files[0].path)"
            :title="shareTitle"
            style="padding: 5px!important;"
          >
            <am-button
              variant="outline-primary"
            >
              <font-awesome-icon
                :icon="['fab', network.icon]"
                size="lg"
                :style="{ color: network.color }"
              />
              {{ network.name }}</am-button>
          </ShareNetwork>
        </div>
      </div>
      <div
        v-if="!shareWithAll"
        class="row"
      >
        <div
          v-for="(reci,i) in recipients"
          :key="i"
          class="col-12"
        >
          <div
            class="d-flex align-items-center"
          >
            <am-profile-photo
              size="30"
              :photo="reci.photo"
            />
            <div style="margin-right: 5px;" />
            <b-badge variant="light-primary">
              {{ reci.name }}
            </b-badge>
            <am-button
              icon-only
              variant="flat-danger"
              icon="XIcon"
              @click="clearContributor(i)"
            />
          </div>
        </div>
      </div>
      <am-button
        label="Clear Form"
        icon="XIcon"
        variant="flat-danger"
        @click="clearForm()"
      />
      <div class="mt-2" />
      <template slot="modal-actions">
        <b-row class="float-right">
          <am-button
            name="cancel"
            variant="flat-danger"
            icon="XIcon"
            class="mr-1"
            label="Cancel"
            @click="closeShareModal()"
          />
          <am-button
            name="save_share"
            label="Share"
            :disabled="invalid || (!shareWithAll && recipients.length<1)"
            :loading="shareLoading"
            loading-text="Loading..."
            @click="shareFiles()"
          />
        </b-row>
      </template>
    </am-modal>
  </validation-observer>
</template>

<script>
import {
  BRow, BBadge, BFormCheckbox,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import logData from '@/libs/log'
import AmInput from '@/farmbusiness/components/AmInput.vue'
import AmSelect from '@/farmbusiness/components/AmSelect.vue'
import AmButton from '@/farmbusiness/components/AmButton.vue'
import AmModal from '@/farmbusiness/components/AmModal.vue'
import showToast from '@/farmbusiness/mixins/showToast'
import ErrorDisplay from '@/farmbusiness/components/AmErrorDisplay.vue'
import AmProfilePhoto from '@/farmbusiness/components/AmProfilePhoto.vue'
import AmTextArea from '@/farmbusiness/components/AmTextArea.vue'
import AmRichText from '@/farmbusiness/components/AmRichText.vue'

export default {
  name: 'ShareFile',
  components: {
    AmRichText,
    AmTextArea,
    AmProfilePhoto,
    ErrorDisplay,
    AmModal,
    AmButton,
    AmSelect,
    AmInput,
    ValidationObserver,
    BRow,
    BBadge,
    BFormCheckbox,
  },
  mixins: [showToast],
  props: {
    modalOpened: {
      type: Boolean,
      default: false,
    },
    files: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      error: {},
      shareModalOpened: false,
      shareLoading: false,
      shareWithAll: false,
      formLoading: false,
      recipientType: null,
      searchTerm: null,
      shareMessage: null,
      shareSubject: null,
      recipients: [],
      shareWith: null,
      shareTitle: null,
      shareOptions: [{ id: 'local', label: 'Members & Societies' }, { id: 'public', label: 'Social Platforms' }],
      networks: [
        {
          network: 'linkedin',
          name: 'LinkedIn',
          icon: 'linkedin',
          color: '#007bb5',
        },
        {
          network: 'twitter',
          name: 'Twitter',
          icon: 'twitter',
          color: '#1da1f2',
        },
        {
          network: 'whatsapp',
          name: 'Whatsapp',
          icon: 'whatsapp',
          color: '#25d366',
        },
        {
          network: 'email',
          name: 'Email',
          icon: 'google-plus',
          color: '#de1d0b',
        },
        {
          network: 'facebook',
          name: 'Facebook',
          icon: 'facebook',
          color: '#1877f2',
        },
      ],
    }
  },
  computed: {

  },
  watch: {
    // shareWithAll(val) {
    //   if (val) {
    //     this.recipients = []
    //   }
    // },
    files(val) {
      if (val.length > 1) {
        this.shareWith = 'local'
      }
    },
    modalOpened(opened) {
      this.shareModalOpened = opened
    },
    id(id) {
      this.selectedContributionId = id
    },
  },
  methods: {
    clearForm() {
      this.shareWithAll = false
      this.shareTitle = null
      this.recipientType = null
      this.shareMessage = null
      this.shareSubject = null
      this.recipients = []
      if (this.files.length > 1) {
        this.shareWith = 'local'
      } else {
        this.shareWith = null
      }
      this.$refs.shareForm.reset()
    },
    addRecipient(data) {
      const present = this.recipients.some(r => r.id === data.id)
      if (!present) {
        this.recipients.push(data)
      }
    },
    getPath(path) {
      return `${process.env.VUE_APP_STORAGE_PATH}/${path}`
    },
    clearContributor(i) {
      this.recipients.splice(i, 1)
    },
    closeShareModal() {
      this.shareModalOpened = false
      this.$emit('modalClosed')
    },
    getRecipientDetails(search) {
      if (search) {
        this.formLoading = true
        if (this.recipientType === 'Members') {
          this.$store.dispatch('documents/getMember', search).then(res => {
            this.addRecipient(res)
          }).catch(err => {
            this.error = err
          }).finally(() => {
            this.formLoading = false
            this.searchTerm = null
          })
        } else {
          this.$store.dispatch('documents/getOrganization', search).then(res => {
            this.addRecipient(res)
          }).catch(err => {
            this.error = err
          }).finally(() => {
            this.formLoading = false
            this.searchTerm = null
          })
        }
      } else {
        this.showInfo('Enter Contributor ID')
      }
    },
    shareFiles() {
      this.error = {}
      if (this.shareWithAll) {
        this.recipients = []
      }
      this.shareLoading = true
      this.$store.dispatch('documents/shareFiles',
        {
          ids: this.recipients.map(rc => rc.id),
          subject: this.shareSubject,
          message: this.shareMessage,
          share_with: this.recipientType,
          share_with_all: this.shareWithAll,
          files: this.files.map(f => ({ name: f.name, path: f.path })),
        })
        .then(res => {
          this.showSuccess('Documents shared successfully')
        })
        .catch(err => {
          logData(err)
          this.error = err
          this.showError('Failed to share documents')
        }).finally(() => {
          this.shareLoading = false
        })
    },
  },
}
</script>
